import {
  Box,
  Button,
  Typography,
  Avatar,
  Paper,
  Grid,
  useTheme,
  useMediaQuery,
  Divider,
  Link,
  Container,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RouteUrls } from "../../Constants/RouteUrls";
import { Fragment, useEffect, useState, useRef } from "react";
import { OtpMethodDataArr, PasswordMethodDataArr } from "./Data";
import { commonFormSwitch } from "../../Utils/SwitchCases";
import { HandleFormChange, ValidateFormData } from "../../Utils/Common";
import authApi from "Service/Auth.api";
import { useDispatch } from "react-redux";
import { setMyInfo } from "../../Redux/User/Action";
import { FormateMyInfoData } from "./Formatter";
import {
  setAlertData,
  startLoading,
  stopLoading,
} from "../../Redux/Screen/Action";
import { Home } from "@mui/icons-material";
import ControlledForm from "Components/ControlledForm";
import OtpInput from "Components/OtpInput";

const LoginPage = () => {
  const [formData, setFormData] = useState(PasswordMethodDataArr);
  const [loginMethod, setLoginMethod] = useState("password");
  const [otpSent, setOtpSent] = useState(false);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const [searchParams] = useSearchParams();

  // 2FA States
  const [is2FA, setIs2FA] = useState(false);
  const [maskedPhone, setMaskedPhone] = useState("");
  const [tempUserData, setTempUserData] = useState(null);
  const [otpValue, setOtpValue] = useState("");

  // Timer State
  const [timer, setTimer] = useState(0);
  const timerInterval = useRef(null);

  const startTimer = () => {
    setTimer(30);
    if (timerInterval.current) clearInterval(timerInterval.current);
    timerInterval.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerInterval.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (timerInterval.current) clearInterval(timerInterval.current);
    };
  }, []);

  const parseAndStoreRedirectData = () => {
    const redirectParam = searchParams.get("redirect");
    if (!redirectParam) return;
    try {
      const decodedRedirect = decodeURIComponent(redirectParam);
      const queryPart = decodedRedirect.split("?")[1];
      if (queryPart) {
        const params = new URLSearchParams(queryPart);
        const encodedOrder = params.get("data") || params.get("order");
        if (encodedOrder) {
          const decodedData = JSON.parse(decodeURIComponent(encodedOrder));
          localStorage.setItem("selectedData", JSON.stringify(decodedData));
        }
      }
    } catch (error) {
      console.error("Error parsing redirect data:", error);
    }
  };

  useEffect(() => {
    parseAndStoreRedirectData();
  }, [searchParams]);

  const finalizeLogin = (data) => {
    const myInfoData = FormateMyInfoData(data);
    dispatch(setMyInfo(myInfoData));
    localStorage.setItem("token", data?.token);
    if (data?.refreshToken) {
      localStorage.setItem("refreshToken", data?.refreshToken);
    }
    localStorage.setItem("x-nlp-userid", myInfoData?.id);
    dispatch(stopLoading());
    redirectAfterLogin();
  };

  const redirectAfterLogin = () => {
    const redirectParam = searchParams.get("redirect");
    let redirectUrl = `/${RouteUrls.dashboard}`;
    const storedData = localStorage.getItem("selectedData");
    try {
      if (storedData) {
        const decodedData = JSON.parse(storedData);
        if (decodedData.isSubscription === true && decodedData.redirectUrl) {
          localStorage.removeItem("selectedData");
          window.location.href = decodedData.redirectUrl;
          return;
        }
      }
      if (redirectParam) redirectUrl = decodeURIComponent(redirectParam);
      localStorage.removeItem("selectedData");
      navigate(redirectUrl, { replace: true });
    } catch (error) {
      localStorage.removeItem("selectedData");
      navigate(`/${RouteUrls.dashboard}`, { replace: true });
    }
  };

  const handleLoginMethodChange = (method) => {
    if (method !== "otp") {
      setOtpSent(false);
      setTimer(0);
      if (timerInterval.current) clearInterval(timerInterval.current);
    }
    setLoginMethod(method);
    setFormData(
      method === "otp"
        ? JSON.parse(JSON.stringify(OtpMethodDataArr))
        : JSON.parse(JSON.stringify(PasswordMethodDataArr))
    );
  };

  const handlePasswordToggle = (fieldId) => {
    setFormData((prev) =>
      prev.map((field) =>
        field.id === fieldId
          ? { ...field, showPassword: !field.showPassword }
          : field
      )
    );
  };

  const validateMobile = (mobile) => {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(mobile);
  };

  const handleSendOTP = async () => {
    const mobileField = formData[0];
    const mobileValue = mobileField.value;

    if (!mobileValue) {
      setFormData(prev => prev.map(f => f.id === mobileField.id ? { ...f, error: true, errorText: "Mobile number is required" } : f));
      return;
    }

    if (!validateMobile(mobileValue)) {
      setFormData(prev => prev.map(f => f.id === mobileField.id ? { ...f, error: true, errorText: "Please enter a valid 10-digit mobile number" } : f));
      return;
    }

    const res = await authApi.mobileSendOtp({
      mobileNumber: mobileValue,
      requestType: "login",
    });
    if (res?.success) {
      setOtpSent(true);
      startTimer();
      setFormData((prev) =>
        prev.map((el) => (el.id === "otp" ? { ...el, readOnly: false } : el))
      );
    }
  };

  const handleVerifyOtp = async () => {
    if (is2FA && otpValue.length !== 4) {
      dispatch(setAlertData({ alertMessage: "Please enter a valid 4-digit OTP", alertVariant: "error", openAlert: true }));
      return;
    }
    if (!is2FA && otpValue.length !== 4) {
      // If using Login with OTP flow, we need to validate separate otp box
      // But wait, the original code used formData[1].value for OTP.
      // We are replacing that with OtpInput state 'otpValue' for better control.
      if (otpValue.length !== 4) {
        dispatch(setAlertData({ alertMessage: "Please enter a valid 4-digit OTP", alertVariant: "error", openAlert: true }));
        return;
      }
    }

    dispatch(startLoading({ loading: true, loadtext: "Verifying OTP..." }));
    const mobileNumber = is2FA ? tempUserData?.phone : formData?.[0]?.value;
    const otpValueToVerify = otpValue; // Use local state for both flows

    const res = await authApi.verifyOtp({
      mobileNumber: mobileNumber,
      email: null,
      otp: otpValueToVerify,
    });
    if (res?.success) {
      dispatch(stopLoading());
      dispatch(
        setAlertData({
          alertMessage: "Otp Verified Successfully",
          alertVariant: "success",
          openAlert: true,
        })
      );
      finalizeLogin(res.data);
    } else {
      dispatch(stopLoading());
    }
  };

  const handlepasswordVerifiction = async () => {
    const verification = ValidateFormData(formData);
    setFormData(verification.formData);
    if (!verification.errorCheck) {
      dispatch(startLoading({ loading: true, loadtext: "Signing In..." }));
      const res = await authApi.signIn({
        username: formData[0].value,
        password: formData[1].value,
      });

      if (res?.success) {
        const myInfoData = FormateMyInfoData(res?.data);
        const role = myInfoData?.userType?.toUpperCase();

        // if (role === "CORPORATEUSER" || role === "PROFESSIONAL") {
        if (false) {
          const otpRes = await authApi.mobileSendOtp({
            mobileNumber: res?.data?.phone,
            requestType: "login",
          });

          if (otpRes?.success) {
            dispatch(stopLoading());
            setTempUserData(res.data);
            const phone = res?.data?.phone || "";
            setMaskedPhone(`XXXXXX${phone.slice(-4)}`);
            setIs2FA(true);
            setFormData(JSON.parse(JSON.stringify(OtpMethodDataArr)));
            setOtpSent(true);
            setOtpValue("");
            startTimer();
          }
        } else {
          finalizeLogin(res.data);
        }
      } else {
        dispatch(stopLoading());
      }
    }
  };

  const handleSignIn = async () => {
    if (loading) return;

    setLoading(true);
    try {
      if (loginMethod === "otp") {
        if (!otpSent) {
          await handleSendOTP();
        } else {
          // Check if user entered OTP
          if (otpValue.length !== 4) {
            // We can't use browser alert, use Redux alert
            dispatch(setAlertData({
              alertMessage: "Please enter the 4-digit OTP sent to your mobile",
              alertVariant: "error",
              openAlert: true
            }));
            setLoading(false);
            return;
          }
          await handleVerifyOtp();
        }
      } else {
        await handlepasswordVerifiction();
      }
    } catch (error) {
      console.error("Sign in error:", error);
      dispatch(stopLoading());
      dispatch(
        setAlertData({
          alertMessage: "Sign in failed. Please try again.",
          alertVariant: "error",
          openAlert: true,
        })
      )
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (timer > 0) return;

    const mobileNumber = is2FA ? tempUserData?.phone : formData?.[0]?.value;

    const res = await authApi.mobileSendOtp({
      mobileNumber: mobileNumber,
      requestType: "login",
    });

    if (res?.success) {
      startTimer();
      dispatch(setAlertData({ alertMessage: "OTP Resent Successfully", alertVariant: "success", openAlert: true }));
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f4f7f9 0%, #e9eef2 100%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      {/* Logo positioned absolutely on top for clean center flow */}
      <Box
        sx={{
          mb: 4,
          textAlign: "center",
        }}
      >
        <Box
          component="img"
          src={"https://nolegalpaisa.com/assets/images/new_logo.webp"}
          alt="Logo"
          sx={{
            width: { xs: "180px", md: "180px" },
            cursor: "pointer",
            transition: "0.3s",
            "&:hover": { transform: "scale(1.02)" },
          }}
          onClick={() => (window.location.href = "/home.html")}
        />
      </Box>

      <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
        <Paper
          elevation={6}
          sx={{
            p: { xs: 3, sm: 6 },
            width: "100%",
            maxWidth: 480,
            borderRadius: 4,
            textAlign: "center",
            boxShadow: "0px 20px 40px rgba(0,0,0,0.05)",
            border: "1px solid rgba(255,255,255,0.8)",
          }}
        >
          <Avatar
            sx={{
              mx: "auto",
              mb: 2,
              bgcolor: "primary.main",
              width: 64,
              height: 64,
              boxShadow: "0 4px 10px rgba(162, 72, 87, 0.2)",
            }}
          >
            <LockOutlinedIcon fontSize="large" />
          </Avatar>

          <Typography variant="h4" fontWeight="700" color="text.primary" gutterBottom>
            {is2FA ? "Verify Account" : (loginMethod === "otp" && otpSent ? "Enter OTP" : "Sign In")}
          </Typography>
          <Typography variant="body2" color="text.primary" sx={{ mb: 4 }}>
            {is2FA
              ? "For your security, please verify your identity"
              : (loginMethod === "otp" && otpSent ? "We've sent a code to your mobile number" : "Welcome back to NoLegalPaisa")}
          </Typography>

          <Box component="form" noValidate sx={{ width: "100%" }}>
            {is2FA ? (
              /* 2FA VIEW */
              <Fragment>
                <Box sx={{ mb: 3, p: 2, bgcolor: "rgba(162, 72, 87, 0.04)", borderRadius: 2, border: '1px dashed #a24857' }}>
                  <Typography variant="body2" color="primary.main">
                    Verification code sent to: <strong>{maskedPhone}</strong>
                  </Typography>
                </Box>

                <OtpInput
                  length={4}
                  value={otpValue}
                  onChange={setOtpValue}
                  onComplete={() => { }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button
                    size="small"
                    disabled={timer > 0}
                    onClick={handleResend}
                    sx={{ textTransform: 'none' }}
                  >
                    {timer > 0 ? `Resend OTP in ${timer}s` : "Resend OTP"}
                  </Button>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ mt: 3, py: 1.8, borderRadius: 2, fontWeight: "bold", fontSize: "1rem" }}
                  onClick={handleVerifyOtp}
                >
                  Confirm & Login
                </Button>
              </Fragment>
            ) : (
              /* STANDARD LOGIN VIEW */
              <Fragment>
                {/* If Login with OTP is selected AND OTP is sent, show OTP input instead of form */}
                {loginMethod === "otp" && otpSent ? (
                  <>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" color="text.primary">
                        Sent to +91 {formData[0].value}
                        <Link component="button" onClick={() => { setOtpSent(false); setOtpValue(""); setFormData(prev => prev.map(f => ({ ...f, readOnly: false }))); }} sx={{ ml: 1, verticalAlign: 'baseline' }}>
                          Change
                        </Link>
                      </Typography>
                    </Box>
                    <OtpInput
                      length={4}
                      value={otpValue}
                      onChange={setOtpValue}
                      onComplete={() => { }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Button
                        size="small"
                        disabled={timer > 0}
                        onClick={handleResend}
                        sx={{ textTransform: 'none' }}
                      >
                        {timer > 0 ? `Resend OTP in ${timer}s` : "Resend OTP"}
                      </Button>
                    </Box>
                  </>
                ) : (
                  /* Normal Form Inputs (Password or Mobile number for OTP) */
                  formData.map((el) => {
                    // If login method is OTP, we only want to show the first field (Mobile) initially
                    // The existing data structure for OTP method might have 2 fields, check Data.js usage
                    // Assuming OtpMethodDataArr has mobile at index 0 and otp at index 1.
                    // We only show index 0 here if otp not sent.
                    if (loginMethod === "otp" && el.id === 'otp') return null;

                    return (
                      <Box key={el.id} sx={{ mb: 2.5 }}>
                        {commonFormSwitch({
                          formObj: {
                            ...el,
                            onPasswordToggle: el.hasPasswordToggle ? () => handlePasswordToggle(el.id) : undefined,
                          },
                          onChange: (data) => setFormData(HandleFormChange(data, formData)),
                        })}
                      </Box>
                    )
                  })
                )}

                {/* Switch Login Method & Forgot Password Links */}
                {!otpSent && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4, mt: -1 }}>
                    <Link
                      component="button"
                      variant="subtitle2"
                      underline="none"
                      type="button"
                      fontWeight="600"
                      onClick={() => handleLoginMethodChange(loginMethod === "otp" ? "password" : "otp")}
                    >
                      {loginMethod === "otp" ? "Use Password" : "Login with OTP"}
                    </Link>
                    <Link
                      component="button"
                      variant="subtitle2"
                      color="error"
                      underline="hover"
                      type="button"
                      onClick={() => navigate(`/${RouteUrls.forgot}`)}
                    >
                      Forgot Password?
                    </Link>
                  </Box>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{
                    py: 1.8,
                    borderRadius: 2,
                    fontWeight: "bold",
                    fontSize: "1rem",
                    boxShadow: "0 6px 20px rgba(162, 72, 87, 0.25)",
                    "&:hover": { boxShadow: "0 8px 25px rgba(162, 72, 87, 0.35)" }
                  }}
                  disabled={loading}
                  onClick={handleSignIn}
                >
                  {loginMethod === "otp" ? (otpSent ? "Verify & Login" : "Send OTP") : "Sign In"}
                </Button>
              </Fragment>
            )}

            {!is2FA && (
              <>
                <Divider sx={{ my: 4 }}>
                  <Typography variant="caption" color="text.primary" sx={{ px: 1, letterSpacing: 1, fontWeight: 700 }}>
                    NEW USER REGISTRATION
                  </Typography>
                </Divider>

                <Grid container spacing={1.5} display="flex" justifyContent="center">
                  {[
                    { label: "Individual", url: RouteUrls.register },
                    { label: "Corporate", url: `${RouteUrls.register}/${RouteUrls.corporate}` },
                    { label: "NRI", url: `${RouteUrls.register}/${RouteUrls.NriSignup}` },
                    { label: "Professional", url: `${RouteUrls.register}/${RouteUrls.professional}` },
                  ].map((item) => (
                    <Grid item xs={6} key={item.label}>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        sx={{
                          borderRadius: 1.5,
                          textTransform: "none",
                          fontSize: '0.8rem',
                          borderColor: 'divider',
                          color: 'text.primary',
                          '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
                        }}
                        onClick={() => navigate(`/${item.url}`)}
                      >
                        {item.label}
                      </Button>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            <Box sx={{ mt: 5 }}>
              <Button
                variant="text"
                startIcon={<Home />}
                color="inherit"
                onClick={() => (window.location.href = "/home.html")}
                sx={{ textTransform: "none", opacity: 0.6, fontSize: '0.9rem' }}
              >
                Back to Homepage
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;